<template>
  <div class="hms-page py-3">
    <header class="hms-toolbar">
      <div class="hms-toolbar__title">
        <span class="hms-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>HMS Imports</h1>
          <p>Upload the daily Pulse Suite patient export for tele-sales incentive attribution</p>
        </div>
      </div>
      <div class="hms-toolbar__actions">
        <CButton size="sm" class="hms-refresh-btn" :class="{ 'is-loading': isLoadingHistory }" :disabled="isLoadingHistory" @click="loadHistory">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hms-refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoadingHistory ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="hms-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="hms-alert">{{ successMessage }}</CAlert>

    <section class="hms-section">
      <header class="hms-section__head">
        <h2>Upload Export</h2>
        <small class="hms-section__hint">XLSX, XLS or CSV — duplicate OP Nos. are skipped automatically</small>
      </header>
      <div class="hms-section__body">
        <form class="hms-filters" @submit.prevent="uploadFile">
          <div class="hms-field hms-field--file">
            <label class="hms-label">Pulse Suite File</label>
            <div class="hms-file">
              <input
                ref="fileInput"
                type="file"
                class="hms-file__input"
                accept=".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                @change="onFileChange"
              >
              <button type="button" class="hms-file__btn" @click="triggerFile">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
                <span>{{ file ? 'Change file' : 'Choose file' }}</span>
              </button>
              <div v-if="file" class="hms-file__chip">
                <span class="hms-file__name" :title="file.name">{{ file.name }}</span>
                <button type="button" class="hms-file__clear" aria-label="Remove file" @click="clearFile">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <span v-else class="hms-file__hint">No file chosen</span>
            </div>
          </div>

          <div class="hms-field hms-field--action">
            <label class="hms-label" aria-hidden="true">&nbsp;</label>
            <button type="submit" class="hms-submit-btn" :disabled="!file || isUploading">
              {{ isUploading ? 'Uploading…' : 'Upload' }}
            </button>
          </div>
        </form>

        <div v-if="lastImport" class="hms-result" :class="lastImportTone">
          <div class="hms-result__head">
            <span class="hms-result__title">{{ lastImport.original_filename }}</span>
            <span class="hms-result__status">{{ lastImportStatusLabel }}</span>
          </div>
          <div class="hms-result__stats">
            <span class="hms-stat"><strong>{{ lastImport.row_count || 0 }}</strong> rows scanned</span>
            <span class="hms-stat hms-stat--new"><strong>{{ lastImport.new_count || 0 }}</strong> new patients</span>
            <span class="hms-stat hms-stat--dup"><strong>{{ lastImport.duplicate_count || 0 }}</strong> duplicates skipped</span>
          </div>
          <p v-if="lastImport.error_message" class="hms-result__error">{{ lastImport.error_message }}</p>
        </div>
      </div>
    </section>

    <BaseTable
      title="Import History"
      :items="historyRows"
      :columns="columns"
      :loading="isLoadingHistory"
      :sortable="false"
      :show-count="false"
      empty-text="No HMS imports yet. Upload your first Pulse Suite export above."
    >
      <template #toolbar>
        <span v-if="pagination.total" class="hms-count">{{ rangeLabel }}</span>
      </template>
      <template #cell-status="{ value }">
        <span class="hms-pill" :class="statusPillClass(value)">{{ statusLabel(value) }}</span>
      </template>
    </BaseTable>

    <div v-if="!isLoadingHistory && historyRows.length && pagination.last_page > 1" class="hms-pagination-bar">
      <span class="hms-pagination-info">{{ rangeLabel }}</span>
      <AppPager
        :current-page="Number(pagination.current_page) || 1"
        :last-page="Number(pagination.last_page) || 1"
        @change="goToPage"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatDateTime (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const day = String(d.getDate()).padStart(2, '0')
  const month = MONTHS_SHORT[d.getMonth()]
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${day} ${month} ${d.getFullYear()}, ${hh}:${mm}`
}

export default {
  name: 'HmsImports',
  components: { BaseTable, AppPager },
  data () {
    return {
      file: null,
      isUploading: false,
      isLoadingHistory: false,
      errorMessage: '',
      successMessage: '',
      lastImport: null,
      imports: [],
      pagination: { current_page: 1, per_page: 25, total: 0, last_page: 1 }
    }
  },
  computed: {
    columns () {
      return [
        { key: 'sno',                label: 'SNo',         width: '70px' },
        { key: 'original_filename',  label: 'Filename' },
        { key: 'uploaded_by_name',   label: 'Uploaded By', width: '180px' },
        { key: 'uploaded_at',        label: 'Uploaded At', width: '180px' },
        { key: 'row_count',          label: 'Rows',        width: '90px',  align: 'right' },
        { key: 'new_count',          label: 'New',         width: '90px',  align: 'right' },
        { key: 'duplicate_count',    label: 'Duplicates',  width: '110px', align: 'right' },
        { key: 'status',             label: 'Status',      width: '120px', align: 'center' }
      ]
    },
    historyRows () {
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      return this.imports.map((row, index) => ({
        sno: (page - 1) * perPage + index + 1,
        original_filename: row.original_filename,
        uploaded_by_name: row.uploader ? row.uploader.name : '—',
        uploaded_at: formatDateTime(row.created_at),
        row_count: row.row_count || 0,
        new_count: row.new_count || 0,
        duplicate_count: row.duplicate_count || 0,
        status: row.status
      }))
    },
    rangeLabel () {
      const total = Number(this.pagination.total) || 0
      if (!total) return '0 imports'
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      const start = (page - 1) * perPage + 1
      const end = Math.min(page * perPage, total)
      return `${start}–${end} of ${total}`
    },
    lastImportStatusLabel () {
      if (!this.lastImport) return ''
      return this.statusLabel(this.lastImport.status)
    },
    lastImportTone () {
      if (!this.lastImport) return ''
      if (this.lastImport.status === 'failed') return 'hms-result--error'
      if (this.lastImport.status === 'completed') return 'hms-result--success'
      return 'hms-result--info'
    }
  },
  created () {
    this.loadHistory()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    triggerFile () {
      const input = this.$refs.fileInput
      if (input) input.click()
    },
    onFileChange (event) {
      this.file = (event.target.files && event.target.files[0]) || null
    },
    clearFile () {
      this.file = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    parseError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data) {
        if (typeof data === 'string') return data
        if (data.message) return data.message
        if (data.errors) {
          const k = Object.keys(data.errors)[0]
          if (k && Array.isArray(data.errors[k])) return data.errors[k][0]
        }
        if (data.error) {
          if (typeof data.error === 'string') return data.error
          const k = Object.keys(data.error)[0]
          if (k && Array.isArray(data.error[k])) return data.error[k][0]
        }
      }
      return fallback
    },
    uploadFile () {
      if (!this.file) {
        this.errorMessage = 'Choose a Pulse Suite export file first.'
        return
      }
      this.isUploading = true
      this.errorMessage = ''
      this.successMessage = ''

      const formData = new FormData()
      formData.append('file', this.file)

      axios.post(`${this.$apiAdress}/api/hms-imports?token=${this.authToken()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.lastImport = data.import || null
          if (this.lastImport && this.lastImport.status === 'completed') {
            const created = this.lastImport.new_count || 0
            const dup = this.lastImport.duplicate_count || 0
            this.successMessage = `Imported ${created} new patient${created === 1 ? '' : 's'}. ${dup} duplicate${dup === 1 ? '' : 's'} skipped.`
          }
          this.clearFile()
          this.loadHistory()
        })
        .catch((error) => {
          console.log(error)
          if (error && error.response && error.response.data && error.response.data.import) {
            this.lastImport = error.response.data.import
          }
          this.errorMessage = this.parseError(error, 'Upload failed. Please check the file and try again.')
        })
        .finally(() => {
          this.isUploading = false
        })
    },
    loadHistory (page) {
      this.isLoadingHistory = true
      const targetPage = Number(page) || Number(this.pagination.current_page) || 1
      axios.get(`${this.$apiAdress}/api/hms-imports`, {
        params: { token: this.authToken(), page: targetPage, per_page: this.pagination.per_page || 25 }
      })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.imports = Array.isArray(data.data) ? data.data : []
          this.pagination = {
            current_page: Number(data.current_page) || 1,
            per_page: Number(data.per_page) || 25,
            total: Number(data.total) || 0,
            last_page: Number(data.last_page) || 1
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to fetch import history.')
        })
        .finally(() => {
          this.isLoadingHistory = false
        })
    },
    goToPage (page) {
      if (Number(page) === Number(this.pagination.current_page)) return
      this.loadHistory(Number(page))
    },
    statusLabel (status) {
      if (status === 'completed') return 'Completed'
      if (status === 'failed') return 'Failed'
      if (status === 'parsing') return 'Parsing'
      return status || '—'
    },
    statusPillClass (status) {
      if (status === 'completed') return 'hms-pill--green'
      if (status === 'failed') return 'hms-pill--red'
      if (status === 'parsing') return 'hms-pill--amber'
      return 'hms-pill--gray'
    }
  }
}
</script>

<style scoped>
.hms-page { padding: 0.25rem 0; }

.hms-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.hms-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.hms-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.hms-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.hms-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.hms-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.85rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  font-weight: 600;
  font-size: 0.82rem;
}

.hms-refresh-btn:hover .hms-refresh-btn__icon {
  transform: rotate(60deg);
  transition: transform 0.25s ease;
}

.hms-refresh-btn.is-loading .hms-refresh-btn__icon {
  animation: hms-spin 0.9s linear infinite;
}

@keyframes hms-spin {
  to { transform: rotate(360deg); }
}

.hms-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.hms-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.hms-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.hms-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.hms-section__hint { color: #6b7280; font-size: 0.78rem; }

.hms-section__body { padding: 1rem; }

.hms-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.hms-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.hms-field { display: flex; flex-direction: column; min-width: 0; }
.hms-field--file { flex: 1 1 320px; min-width: 260px; }
.hms-field--action { flex: 0 0 auto; min-width: 130px; }

.hms-file {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 36px;
  flex-wrap: wrap;
}

.hms-file__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.hms-file__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.hms-file__btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.hms-file__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.hms-file__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
  padding: 0.25rem 0.4rem 0.25rem 0.65rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #166534;
}

.hms-file__name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hms-file__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 0;
  background: rgba(22, 101, 52, 0.12);
  color: #166534;
  cursor: pointer;
}

.hms-file__hint {
  color: #94a3b8;
  font-size: 0.8rem;
  font-style: italic;
}

.hms-submit-btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  padding: 0 1.25rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  cursor: pointer;
}

.hms-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.hms-submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.hms-result {
  margin-top: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.hms-result--success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.hms-result--error {
  background: #fef2f2;
  border-color: #fecaca;
}

.hms-result__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.hms-result__title {
  font-weight: 600;
  color: #1f2a44;
  font-size: 0.88rem;
}

.hms-result__status {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #475569;
}

.hms-result__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.82rem;
  color: #475569;
}

.hms-stat strong { color: #1f2a44; }
.hms-stat--new strong { color: #166534; }
.hms-stat--dup strong { color: #b45309; }

.hms-result__error {
  margin: 0.5rem 0 0;
  font-size: 0.82rem;
  color: #b91c1c;
}

.hms-pill {
  display: inline-block;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.hms-pill--green { background: #dcfce7; color: #166534; }
.hms-pill--red   { background: #fee2e2; color: #991b1b; }
.hms-pill--amber { background: #fef3c7; color: #92400e; }
.hms-pill--gray  { background: #e5e7eb; color: #374151; }

.hms-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.hms-pagination-info {
  font-size: 0.8rem;
  color: #6b7280;
}

.hms-count {
  font-size: 0.78rem;
  color: #6b7280;
}
</style>
